import {
	buildRfxListPath,
	getRfxTabsForScope,
	isRfxListTabForScope,
	RFX_DEFAULT_TAB,
	RFX_SCOPE_TITLES,
	type RfxTabConfig,
} from "../tabs/rfx-tab-config";
import {
	useRfxList,
	type RfxListFilterOptions,
	type RfxListPageMeta,
	type RfxListQueryFilters,
	type RfxListTabCounts,
} from "../hooks/useRfxList";
import type {
	RfxListItemResource,
	RfxListScope,
	RfxListTab,
} from "../types/rfx-list.type";

/**
 * The slice of a list query the shell actually consumes.
 *
 * Narrower than `useRfxList`'s return on purpose: it is the contract a surface
 * must satisfy, and the bidder surface is backed by a DIFFERENT endpoint
 * (`GET /rfq/supplier-index`). Pinning the shell to this shape rather than to
 * one hook's return type is what lets a second data source plug in without the
 * shell learning about it.
 */
export interface RfxListSurfaceData<TTab extends string, TRow> {
	counts: RfxListTabCounts<TTab>;
	filterOptions: RfxListFilterOptions;
	isError: boolean;
	isLoading: boolean;
	meta?: RfxListPageMeta;
	rows: TRow[];
}

/**
 * A "surface" is one instance of the shared RFx list shell (`RfxListPage`).
 *
 * The shell is already agnostic about rows (it is generic over `T` and takes a
 * `renderList` slot), but four things were still resolved from `RfxListScope`
 * directly inside it: the tab set, the page title, the tab-link path builder
 * and the default tab. That hard-wired the shell to the three PROCURER scopes,
 * so the bidder list could not reuse it and grew a parallel implementation
 * instead — with its own tab control, its own infinite scroll and no search,
 * sort or paging.
 *
 * Pulling those four into a descriptor turns "which list am I" into data. The
 * shell reads a surface; it no longer knows the scope names. Adding a list
 * means registering a descriptor, not editing the shell.
 *
 * The descriptor also owns the DATA SOURCE (`useList`). That was the last
 * thing the shell resolved from `scope` itself, and it is the one the bidder
 * list actually differs on — it reads `GET /rfq/supplier-index`, not
 * `GET /rfq?scope=`.
 *
 * Tab keys are a TYPE PARAMETER, not the shared `RfxListTab` union.
 *
 * `RfxListTab` mirrors `App\Enums\RfxListTab` element-for-element and must keep
 * doing so — it is the wire contract for the three procurer scopes. The bidder
 * list has its own keys (all / accepted / submitted / awarded / unsuccessful)
 * which are submission states, not RFx list tabs, and do not exist in that
 * backend enum. Widening the shared union to fit them would put values in it
 * that the API will never send and quietly break its mirror property.
 *
 * So each surface declares its own key type and the shell stays generic over
 * it. The shell never compares a tab key against a fixed list — it asks the
 * surface (`isValidTab`).
 */
export interface RfxListSurface<
	TTab extends string = RfxListTab,
	TRow = RfxListItemResource,
> {
	/*
	 * NOTE: there is deliberately no `apiScope` here. It was carried while the
	 * shell still built the request itself; now that `useList` owns the data
	 * source, nothing read it — and the bidder surface has no `RfxListScope` at
	 * all (it reads `GET /rfq/supplier-index`, which takes no scope). Keeping it
	 * would have forced that surface to name a procurer scope it never sends.
	 */
	/** Tab to fall back to when the route segment is missing or invalid. */
	defaultTab: TTab;
	/** Tab-link href builder — the route shape is per-surface, not global. */
	buildPath: (companyId: number | string, tab: TTab) => string;
	/**
	 * Whether a raw route segment is a tab of THIS surface. Owned by the
	 * surface because validity is per-list: "evaluation" is a real tab of
	 * Tenders & Quotes and a 404 for Approvals.
	 */
	isValidTab: (tab: string | undefined) => tab is TTab;
	/** Tab bar contents, in render order. */
	tabs: RfxTabConfig<TTab>[];
	title: string;
	/**
	 * Where the rows come from. A hook, because every current and planned
	 * source is a react-query hook and the shell calls it unconditionally at a
	 * fixed position in its render — so the rules of hooks hold.
	 *
	 * This is the last thing the shell resolved from `scope` itself. With it in
	 * the descriptor, the shell no longer knows *which* endpoint backs the list
	 * it is rendering, which is precisely what the bidder list needs: same
	 * chrome, same search/sort/paging, different origin.
	 */
	useList: (
		tab: TTab,
		filters: RfxListQueryFilters
	) => RfxListSurfaceData<TTab, TRow>;
}

/**
 * Descriptor for one of the three procurer scopes.
 *
 * Every field delegates to the existing `rfx-tab-config` helpers rather than
 * restating anything, so the three current pages resolve byte-identical values
 * to what the shell computed inline before. This is the no-op half of the
 * consolidation.
 */
export const getRfxListSurface = <TRow = RfxListItemResource,>(
	scope: RfxListScope
): RfxListSurface<RfxListTab, TRow> => ({
	defaultTab: RFX_DEFAULT_TAB,
	buildPath: (companyId, tab) => buildRfxListPath(companyId, scope, tab),
	isValidTab: (tab): tab is RfxListTab => isRfxListTabForScope(scope, tab),
	tabs: getRfxTabsForScope(scope),
	title: RFX_SCOPE_TITLES[scope],
	useList: (tab, filters) => useRfxList<TRow, RfxListTab>(scope, tab, filters),
});
